import { useRef } from "react";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { m, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Sun } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG, IMG_ALT, SITE_ORIGIN, UNITS } from "@/data/site";
import { MaskedLines, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import HomeUnits from "@/components/HomeUnits";
import HomeReviews from "@/components/HomeReviews";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

// Rating and review count follow the reviews managed in the admin panel
// instead of numbers typed once in the copy.
const REVIEWS_TOTAL = UNITS.reduce((sum, u) => sum + u.reviewsCount, 0);
const RATING_AVG = REVIEWS_TOTAL
  ? UNITS.reduce((sum, u) => sum + Number(u.rating) * u.reviewsCount, 0) / REVIEWS_TOTAL
  : 0;
const liveStat = (s, i) => {
  if (i === 0) return { ...s, value: `${(Math.floor(RATING_AVG * 10) / 10).toFixed(1)}+` };
  if (i === 1) return { ...s, value: String(REVIEWS_TOTAL) };
  return s;
};

const Home = () => {
  const { t, lang } = useLang();
  usePageMeta({ title: t.seo.home.title, description: t.seo.home.description });

  // Media pesata sulle recensioni realmente visibili di entrambe le
  // unità (non un numero fisso) — resta corretta se l'admin nasconde
  // una recensione o cambia i prezzi base.
  const allReviews = UNITS.flatMap((u) => u.reviews);
  const avgRating = (allReviews.reduce((sum, r) => sum + r.rating, 0) / allReviews.length).toFixed(2);
  const basePrices = UNITS.map((u) => u.pricing.basePrice);
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: "Postumia Place",
    url: SITE_ORIGIN,
    image: `${SITE_ORIGIN}${IMG.hero}`,
    telephone: t.footer.phone,
    email: t.footer.email,
    priceRange: `€${Math.min(...basePrices)}–€${Math.max(...basePrices)}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Via Fratelli Bianchin 13",
      postalCode: "31038",
      addressLocality: "Postioma di Paese",
      addressRegion: "TV",
      addressCountry: "IT",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount: allReviews.length,
    },
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="px-3 md:px-6 pt-24 md:pt-28" data-testid="home-hero">
        <div className="relative min-h-[86vh] md:min-h-[560px] rounded-[2rem] overflow-hidden flex flex-col justify-end">
          <m.img
            src={IMG.hero}
            alt={IMG_ALT.hero[lang] || IMG_ALT.hero.it}
            fetchPriority="high"
            style={{ y: imgY }}
            initial={{ scale: 1.14 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
          <div className="absolute inset-0 bg-ink/30" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink/70 via-ink/20 to-transparent" />
          {/* Extra shade only behind the text block, so the title stays readable over leaves and light wood. */}
          <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/45 to-ink/10 md:bg-gradient-to-r md:from-ink/70 md:via-ink/35 md:to-transparent" />

          <div className="relative flex flex-col justify-end px-6 pt-28 pb-8 md:p-14 lg:p-20">
            <m.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-cream mb-5 [text-shadow:0_1px_10px_rgba(42,36,33,0.75)]"
              data-testid="hero-overline"
            >
              {t.hero.overline}
            </m.p>
            <h1 className="font-display font-medium text-cream text-[2.6rem] sm:text-6xl lg:text-8xl tracking-tight leading-[0.95] text-balance" data-testid="hero-title">
              <MaskedLines lines={t.hero.lines} />
            </h1>
            <m.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-6 max-w-xl text-cream text-base md:text-lg font-light leading-relaxed [text-shadow:0_1px_10px_rgba(42,36,33,0.6)]"
              data-testid="hero-subtitle"
            >
              {t.hero.sub}
            </m.p>
            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/prenota"
                data-testid="hero-book-button"
                className="group inline-flex items-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm md:text-base font-medium text-cream hover:bg-terracotta-dark transition-colors duration-300"
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              {t.ecoBadge.enabled && (
                <span
                  data-testid="eco-badge"
                  className="inline-flex items-center gap-2 rounded-full border border-olive/40 bg-olive/15 px-5 py-2.5 text-sm font-medium text-cream backdrop-blur-sm"
                >
                  <Sun className="h-4 w-4 text-olive" />
                  {t.ecoBadge.text}
                </span>
              )}
            </m.div>
          </div>

          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1 }}
            className="absolute bottom-8 right-8 md:right-14 hidden md:flex items-center gap-3 text-cream [text-shadow:0_1px_10px_rgba(42,36,33,0.75)] drop-shadow-[0_1px_6px_rgba(42,36,33,0.6)]"
            aria-hidden="true"
          >
            <span className="text-xs uppercase tracking-[0.25em]">{t.hero.scroll}</span>
            <m.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
              <ArrowDown className="h-4 w-4" />
            </m.span>
          </m.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-14 md:mt-20" data-testid="home-stats">
        <div className="grid grid-cols-3 max-w-4xl mx-auto text-center">
          {t.stats.map(liveStat).map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className={`px-2 md:px-8 ${i > 0 ? "border-l border-linen" : ""}`}>
              <p className="font-display text-4xl sm:text-5xl md:text-6xl text-terracotta lining-nums">{s.value}</p>
              <p className="mt-2 text-[11px] md:text-sm uppercase tracking-[0.14em] md:tracking-[0.18em] text-stone text-balance">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="mt-16 md:mt-24">
        <Marquee />
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32" data-testid="home-manifesto">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{t.manifesto.overline}</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-4 max-w-2xl">{t.manifesto.title}</h2>
        </Reveal>

        <Reveal className="mt-10 md:mt-14" delay={0.05}>
          <div className="overflow-hidden rounded-2xl aspect-[4/3] md:aspect-[21/8]">
            <img
              src={IMG.patio}
              alt={IMG_ALT.patio[lang] || IMG_ALT.patio.it}
              loading="lazy"
              className="w-full h-full object-cover object-[center_60%]"
            />
          </div>
        </Reveal>
        <div className="mt-10 md:mt-14 grid md:grid-cols-3 gap-10 md:gap-12">
          {t.manifesto.chapters.map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i} className="border-t border-linen pt-6">
              <h3 className="font-display text-3xl md:text-4xl tracking-tight">{c.title}</h3>
              <p className="mt-4 text-stone leading-relaxed font-light">{c.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <HomeUnits />

      <HomeReviews />

      <section className="px-3 md:px-6" data-testid="home-cta">
        <Reveal>
          <div className="rounded-[2rem] bg-terracotta text-cream px-8 md:px-16 py-16 md:py-24 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <div className="max-w-2xl">
              <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-tight">{t.homeCta.title}</h2>
              <p className="mt-5 text-cream text-base md:text-lg font-light">{t.homeCta.sub}</p>
            </div>
            <Link
              to="/prenota"
              data-testid="home-cta-book-button"
              className="group shrink-0 self-start md:self-auto inline-flex items-center gap-3 rounded-full bg-cream text-ink px-8 py-4 text-sm md:text-base font-medium hover:bg-sand transition-colors duration-300"
            >
              {t.homeCta.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
